import { supabase } from './supabaseClient';
import { formatDateForDB, parseDateFromDB } from '../utils/dateUtils';
import type { TaskRow, Task, TaskCreateInput, TaskUpdateInput, DependencyLag, TaskFormatting } from '../types';

// A travessia string do banco <-> Date mora em `utils/dateUtils`, junto do resto
// da matemática de data. Reexportado aqui porque metade do app já importa
// `parseDateFromDB` deste módulo.
export { formatDateForDB, parseDateFromDB };

export const taskService = {
  // Buscar todas as tarefas de um projeto
  async getByProject(projectId: string): Promise<TaskRow[]> {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('project_id', projectId)
      .order('position', { ascending: true })
      .order('created_at', { ascending: true })
      .order('id', { ascending: true });

    if (error) {
      // Ignorar AbortError - causado pelo React StrictMode
      if (error.message?.includes('AbortError')) {
        console.log('[taskService] getByProject AbortError ignorado');
        return [];
      }
      throw error;
    }
    return data as TaskRow[];
  },

  // Criar nova tarefa
  async create(task: TaskCreateInput): Promise<TaskRow | null> {
    const { data, error } = await supabase
      .from('tasks')
      .insert([{
        project_id: task.project_id,
        parent_id: task.parent_id || null,
        name: task.name,
        type: task.type || 'task',
        duration: task.duration || '1d',
        start_date: formatDateForDB(task.start_date || task.startDate || null),
        end_date: formatDateForDB(task.end_date || task.endDate || null),
        progress: task.progress || 0,
        color: task.color ?? null,
        assigned: task.assigned || null,
        priority: task.priority || 0,
        description: task.description || '',
        dependencies: task.dependencies || [],
        dependency_lags: task.dependency_lags || task.dependencyLags || {},
        position: task.position || 0,
        expanded: task.expanded !== false,
        custom_fields: task.custom_fields || task.customFields || {},
        formatting: task.formatting || {},
        status: task.status ?? 'not_started',
        kanban_status: task.kanban_status ?? null,
        kanban_position: task.kanban_position ?? 0,
        sprint_id: task.sprint_id ?? null,
        estimated_hours: task.estimated_hours ?? null
      }])
      .select()
      .single();

    if (error) {
      // Ignorar AbortError - causado pelo React StrictMode
      if (error.message?.includes('AbortError')) {
        console.log('[taskService] create AbortError ignorado');
        return null;
      }
      throw error;
    }
    return data as TaskRow;
  },

  // Criar múltiplas tarefas (para importar dados iniciais)
  async createMany(tasks: TaskCreateInput[]): Promise<TaskRow[]> {
    const { data, error } = await supabase
      .from('tasks')
      .insert(tasks.map(task => ({
        project_id: task.project_id,
        parent_id: task.parent_id || null,
        name: task.name,
        type: task.type || 'task',
        duration: task.duration || '1d',
        start_date: formatDateForDB(task.start_date || task.startDate || null),
        end_date: formatDateForDB(task.end_date || task.endDate || null),
        progress: task.progress || 0,
        color: task.color ?? null,
        assigned: task.assigned || null,
        priority: task.priority || 0,
        description: task.description || '',
        dependencies: task.dependencies || [],
        dependency_lags: task.dependency_lags || task.dependencyLags || {},
        position: task.position || 0,
        expanded: task.expanded !== false,
        custom_fields: task.custom_fields || task.customFields || {},
        formatting: task.formatting || {},
        status: task.status ?? 'not_started',
        kanban_status: task.kanban_status ?? null,
        kanban_position: task.kanban_position ?? 0,
        sprint_id: task.sprint_id ?? null
      })))
      .select();

    if (error) throw error;
    return data as TaskRow[];
  },

  // Atualizar tarefa
  async update(id: string, updates: TaskUpdateInput): Promise<TaskRow | null> {
    // Campos válidos na tabela do Supabase (inclui custom_fields e formatting)
    const validFields: string[] = [
      'name', 'type', 'duration', 'start_date', 'end_date', 'progress',
      'color', 'assigned', 'priority', 'description', 'dependencies',
      'dependency_lags', 'position', 'expanded', 'parent_id', 'project_id',
      'custom_fields', 'formatting', 'status', 'kanban_status', 'kanban_position', 'kanban_entered_at', 'sprint_id',
      'baseline_start_date', 'baseline_end_date', 'baseline_duration',
      'estimated_hours'
    ];

    // Campos computados que não devem ser enviados ao banco (convertidos manualmente para snake_case)
    const computedFields: string[] = ['children', 'hasChildren', 'level', 'startDate', 'endDate', 'baselineStartDate', 'baselineEndDate', 'customFields', 'dependencyLags'];

    const updateData: Record<string, unknown> = {};

    // Converter nomes de campo do formato camelCase para snake_case
    // e formatar datas para preservar o fuso horário local
    if (updates.startDate) {
      updateData.start_date = formatDateForDB(updates.startDate as Date | string);
    }
    if (updates.endDate) {
      updateData.end_date = formatDateForDB(updates.endDate as Date | string);
    }
    if (updates.customFields) {
      updateData.custom_fields = updates.customFields;
    }
    if (updates.dependencyLags !== undefined) {
      updateData.dependency_lags = updates.dependencyLags;
    }

    // Copiar campos válidos
    Object.keys(updates).forEach(key => {
      if (validFields.includes(key) && !computedFields.includes(key)) {
        // Formatar datas para preservar fuso horário local
        if (key === 'start_date' || key === 'end_date') {
          updateData[key] = formatDateForDB((updates as Record<string, unknown>)[key] as Date | string | null);
        } else {
          updateData[key] = (updates as Record<string, unknown>)[key];
        }
      }
    });

    // Se não houver campos válidos para atualizar, retornar sem fazer requisição
    if (Object.keys(updateData).length === 0) {
      return null;
    }

    // [CFBUG] log temporário — remover após validar bug de duas colunas customizadas
    if (updateData.custom_fields !== undefined) {
      console.log('[CFBUG] taskService.update payload', { id, custom_fields: updateData.custom_fields });
    }

    const { data, error } = await supabase
      .from('tasks')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as TaskRow;
  },

  // Recriar uma tarefa preservando o ID original (usado pelo undo de exclusão).
  // Usa upsert: se a linha ainda existir, atualiza; senão, insere com o ID dado.
  // Preservar o ID é essencial porque dependências e parent_id de outras tarefas
  // apontam para ele.
  async restore(task: Record<string, unknown>): Promise<TaskRow | null> {
    const row = {
      id: task.id,
      project_id: task.project_id,
      parent_id: (task.parent_id ?? task.parentId ?? null) as string | null,
      name: task.name,
      type: task.type || 'task',
      duration: task.duration || '1d',
      start_date: formatDateForDB((task.start_date ?? task.startDate ?? null) as Date | string | null),
      end_date: formatDateForDB((task.end_date ?? task.endDate ?? null) as Date | string | null),
      progress: task.progress || 0,
      color: task.color ?? null,
      assigned: task.assigned || null,
      priority: task.priority || 0,
      description: task.description || '',
      dependencies: task.dependencies || [],
      dependency_lags: task.dependency_lags ?? task.dependencyLags ?? {},
      position: task.position || 0,
      expanded: task.expanded !== false,
      custom_fields: task.custom_fields ?? task.customFields ?? {},
      formatting: task.formatting || {},
      status: task.status ?? 'not_started',
      kanban_status: task.kanban_status ?? null,
      kanban_position: task.kanban_position ?? 0,
      sprint_id: task.sprint_id ?? null,
      estimated_hours: task.estimated_hours ?? null
    };

    const { data, error } = await supabase
      .from('tasks')
      .upsert([row])
      .select()
      .single();

    if (error) throw error;
    return data as TaskRow;
  },

  // Deletar tarefa
  async delete(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  // Deletar todas as tarefas de um projeto
  async deleteByProject(projectId: string): Promise<boolean> {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('project_id', projectId);

    if (error) throw error;
    return true;
  },

  // `getPublicByProject` foi removida em 28/08/2026 e NÃO deve voltar.
  //
  // Ela era `from('tasks').select('*')` e servia a página pública `/share/:token`. Duas
  // coisas erradas ao mesmo tempo: a RLS de `tasks` só enxerga quem tem sessão, então o
  // visitante recebia zero linha; e, se a policy fosse reaberta, ela devolveria a linha
  // INTEIRA (horas, custo, responsável) para qualquer um com o link.
  // O cronograma público agora vem de `projectService.getSharedProjectData()`, que chama
  // uma RPC com lista branca de colunas montada no servidor.

  // Salvar baseline: snapshot de datas + esforço + progresso
  // (PMBoK: baseline é o PMB — Performance Measurement Baseline)
  async saveBaseline(projectId: string): Promise<void> {
    // Buscar todas as tasks do projeto com todos os campos a snapshotar
    const { data: tasks, error: fetchError } = await supabase
      .from('tasks')
      .select('id, start_date, end_date, duration, estimated_hours, progress')
      .eq('project_id', projectId);

    if (fetchError) throw fetchError;
    if (!tasks || tasks.length === 0) return;

    // Atualizar cada task com seus dados de baseline
    const updates = tasks.map(task =>
      supabase
        .from('tasks')
        .update({
          baseline_start_date: task.start_date,
          baseline_end_date: task.end_date,
          baseline_duration: task.duration,
          baseline_estimated_hours: task.estimated_hours,
          baseline_progress: task.progress
        })
        .eq('id', task.id)
    );

    const results = await Promise.all(updates);
    const failed = results.find(r => r.error);
    if (failed?.error) throw failed.error;
  },

  // Limpar baseline de todas as tasks do projeto
  async clearBaseline(projectId: string): Promise<void> {
    const { error } = await supabase
      .from('tasks')
      .update({
        baseline_start_date: null,
        baseline_end_date: null,
        baseline_duration: null,
        baseline_estimated_hours: null,
        baseline_progress: null
      })
      .eq('project_id', projectId);

    if (error) throw error;
  }
};

// Função auxiliar para converter tarefas do banco para estrutura hierárquica
export function buildTaskTree(flatTasks: TaskRow[]): Task[] {
  const taskMap = new Map<string, Task>();
  const rootTasks: Task[] = [];

  // Primeiro, criar um mapa de todas as tarefas
  flatTasks.forEach(task => {
    taskMap.set(task.id, {
      ...task,
      // Converter nomes de campo do banco para o formato do app
      // Usar parseDateFromDB para preservar fuso horário local
      startDate: parseDateFromDB(task.start_date),
      endDate: parseDateFromDB(task.end_date),
      baselineStartDate: parseDateFromDB(task.baseline_start_date),
      baselineEndDate: parseDateFromDB(task.baseline_end_date),
      customFields: task.custom_fields || {},
      dependencyLags: task.dependency_lags || {},
      formatting: task.formatting || {},
      children: []
    });
  });

  // Depois, construir a árvore
  flatTasks.forEach(task => {
    const taskNode = taskMap.get(task.id)!;
    if (task.parent_id && taskMap.has(task.parent_id)) {
      taskMap.get(task.parent_id)!.children.push(taskNode);
    } else {
      rootTasks.push(taskNode);
    }
  });

  // Ordenar por position
  const sortByPosition = (a: Task, b: Task): number => (a.position || 0) - (b.position || 0);
  rootTasks.sort(sortByPosition);
  taskMap.forEach(task => task.children.sort(sortByPosition));

  return rootTasks;
}

// Função auxiliar para converter árvore de tarefas para formato flat (para salvar no banco)
export function flattenTaskTree(tasks: Task[], projectId: string, parentId: string | null = null, startPosition: number = 0): Record<string, unknown>[] {
  let flatTasks: Record<string, unknown>[] = [];
  let position = startPosition;

  tasks.forEach(task => {
    const flatTask: Record<string, unknown> = {
      id: task.id,
      project_id: projectId,
      parent_id: parentId,
      name: task.name,
      type: task.type || 'task',
      duration: task.duration,
      start_date: formatDateForDB(task.startDate) || task.start_date,
      end_date: formatDateForDB(task.endDate) || task.end_date,
      progress: task.progress || 0,
      color: task.color,
      assigned: task.assigned,
      priority: task.priority || 0,
      description: task.description || '',
      dependencies: task.dependencies || [],
      dependency_lags: task.dependencyLags || task.dependency_lags || {},
      position: position++,
      expanded: task.expanded !== false,
      custom_fields: task.customFields || task.custom_fields || {},
      formatting: task.formatting || {}
    };

    flatTasks.push(flatTask);

    if (task.children && task.children.length > 0) {
      const childTasks = flattenTaskTree(task.children, projectId, task.id, 0);
      flatTasks = flatTasks.concat(childTasks);
    }
  });

  return flatTasks;
}
